// LevelParser converts raw level data into a LevelDescription.

import { getInstructionType, InstructionCounts } from 'app/src/engine/Instruction';
import MovableItem, {
	getMovableItemOrientation,
	getTurnsFromOrientation,
	MovableItemLocation,
	MovableItemOrientation,
} from 'app/src/engine/movable-items/MovableItem';
import Robot from 'app/src/engine/movable-items/Robot';
import Star, { StarLocation } from 'app/src/engine/Star';
import Tile from 'app/src/engine/tiles/Tile';
import { LevelDescription } from 'app/src/LevelDescription';
import GamePlugin from 'app/src/plugins/GamePlugin';
import GamePluginLoader from 'app/src/plugins/GamePluginLoader';

type RawData = {
	[key: string]: any;
};

export default class LevelParser {
	public static sharedInstance = new LevelParser();

	public parse(levelData: RawData): LevelDescription {
		const transformedLevelData = this._transformLevelDataForSchema(levelData);
		const gamePlugins = this._getGamePlugins(transformedLevelData);

		const tiles = this._getTiles(transformedLevelData, gamePlugins);

		const { robots, robotLocations } = this._getRobots(transformedLevelData, gamePlugins);
		const { movableItems, movableItemLocations } = this._getMovableItems(
			transformedLevelData,
			gamePlugins,
		);

		return {
			title: this._getTitle(transformedLevelData),
			description: this._getDescription(transformedLevelData),
			tiles,
			stars: this._getStars(transformedLevelData),
			robots,
			robotLocations,
			movableItems,
			movableItemLocations,
			gamePlugins,
			instructionCounts: this._getInstructions(transformedLevelData),
		};
	}

	private _transformLevelDataForSchema(levelData: RawData): RawData {
		if (levelData['schemaVersion'] === 1) {
			// Make a deep clone of the data so we're not transforming the argument.
			const levelDataClone = JSON.parse(JSON.stringify(levelData));

			// Schema version 1 specifies rows in reverse order. Reverse them.
			levelDataClone['grid'].reverse();

			// Reverse the coordinates of items that are placed on the map.
			const totalRows = levelDataClone['grid'].length;

			if (levelDataClone['stars']) {
				for (const star of levelDataClone['stars']) {
					star.row = totalRows - 1 - star.row;
				}
			}
			if (levelDataClone['robots']) {
				for (const robot of levelDataClone['robots']) {
					robot.location.row = totalRows - 1 - robot.location.row;
				}
			}
			if (levelDataClone['movableItems']) {
				for (const movableItem of levelDataClone['movableItems']) {
					movableItem.location.row = totalRows - 1 - movableItem.location.row;
				}
			}

			return levelDataClone;
		}

		return levelData;
	}

	private _getGamePlugins(levelData: RawData): Set<GamePlugin> {
		const gamePluginNames = levelData['plugins'];
		if (!Array.isArray(gamePluginNames)) {
			throw new Error('No plugins in level data.');
		}

		const gamePlugins: Set<GamePlugin> = new Set();

		for (const gamePluginName of gamePluginNames) {
			const gamePlugin = GamePluginLoader.sharedInstance.loadPlugin(gamePluginName);
			gamePlugins.add(gamePlugin);
		}

		return gamePlugins;
	}

	private _getTitle(levelData: RawData): string {
		const name = levelData['name'];

		if (typeof name !== 'string') {
			throw new Error('No level name in data.');
		}

		return name;
	}

	private _getDescription(levelData: RawData): string {
		const description = levelData['description'];

		if (typeof description !== 'string') {
			throw new Error('No level description in data.');
		}

		return description;
	}

	private _getStars(levelData: RawData): Set<Star> {
		const starLocations: StarLocation[] = levelData['stars'] || [];

		if (!Array.isArray(starLocations)) {
			throw new Error('Invalid stars data.');
		}

		const stars = new Set<Star>();

		for (const starLocation of starLocations) {
			stars.add(new Star(starLocation));
		}

		return stars;
	}

	private _getTileForData(tileData: RawData, gamePlugins: Set<GamePlugin>): Tile {
		const tileType = tileData['tile'];
		if (typeof name !== 'string') {
			throw new Error('No MovableItem type in data.');
		}

		const tileProvider = Array.from(gamePlugins).find((plugin) =>
			plugin.getListOfProvidedTiles().includes(tileType),
		);
		if (!tileProvider) {
			throw new Error('No available Tile provider for Tile type.');
		}

		return tileProvider.getTile(tileData);
	}

	private _getTiles(levelData: RawData, gamePlugins: Set<GamePlugin>): Tile[][] {
		const gridData = levelData['grid'];

		if (!Array.isArray(gridData)) {
			throw new Error('No tiles in level data.');
		}

		const tiles = [];

		// Iterate over the description and create tiles.
		for (const rowData of gridData) {
			const tileRow: Tile[] = [];

			for (const tileData of rowData) {
				const tile = this._getTileForData(tileData, gamePlugins);
				tileRow.push(tile);
			}

			tiles.push(tileRow);
		}

		// Make sure all rows have the same number of tiles.
		let numberOfTilesInRow: number | null = null;
		for (const row of tiles) {
			if (numberOfTilesInRow === null) {
				numberOfTilesInRow = row.length;
			} else {
				if (row.length !== numberOfTilesInRow) {
					throw new Error('Grid data has uneven rows.');
				}
			}
		}

		return tiles;
	}

	private _getMovableItemForData(
		movableItemData: RawData,
		gamePlugins: Set<GamePlugin>,
	): { movableItem: MovableItem; movableItemLocation: MovableItemLocation } {
		const movableItemType = movableItemData['movableItem'];
		if (typeof name !== 'string') {
			throw new Error('No MovableItem type in data.');
		}

		const movableItemProvider = Array.from(gamePlugins).find((plugin) =>
			plugin.getListOfProvidedMovableItems().includes(movableItemType),
		);
		if (!movableItemProvider) {
			throw new Error('No available MovableItem provider for MovableItem type.');
		}

		const movableItem = movableItemProvider.getMovableItem(movableItemData);

		const movableItemLocationDescription = this._getLocationData(movableItemData);
		const movableItemLocation = this._getMovableItemLocation(movableItemLocationDescription);
		if (!movableItemLocation) {
			throw new Error('Missing location for MovableItem');
		}

		return { movableItem, movableItemLocation };
	}

	private _getMovableItemLocation(locationData: RawData): MovableItemLocation | null {
		let row: number | null = null;
		let col: number | null = null;

		if (typeof locationData['row'] === 'number') {
			row = locationData['row'];
		}
		if (typeof locationData['col'] === 'number') {
			col = locationData['col'];
		}

		let orientation: MovableItemOrientation | null = null;
		if (typeof locationData['orientation'] === 'string') {
			orientation = getMovableItemOrientation(locationData['orientation']);
		}

		if (row === null || col === null || orientation === null) {
			return null;
		}

		return { row, col, orientation, turns: getTurnsFromOrientation(orientation) };
	}

	private _getRobots(
		levelData: RawData,
		gamePlugins: Set<GamePlugin>,
	): { robots: Set<Robot>; robotLocations: Map<Robot, MovableItemLocation> } {
		const robotsData = levelData['robots'] || [];

		if (!Array.isArray(robotsData)) {
			throw new Error('Invalid robots data.');
		}

		const robots = new Set<Robot>();
		const robotLocations = new Map<Robot, MovableItemLocation>();

		for (const robotData of robotsData) {
			const { movableItem, movableItemLocation } = this._getMovableItemForData(
				robotData,
				gamePlugins,
			);

			robots.add(movableItem as Robot);
			robotLocations.set(movableItem as Robot, movableItemLocation);
		}

		return { robots, robotLocations };
	}

	private _getMovableItems(
		levelData: RawData,
		gamePlugins: Set<GamePlugin>,
	): {
		movableItems: Set<MovableItem>;
		movableItemLocations: Map<MovableItem, MovableItemLocation>;
	} {
		const movableItemsData = levelData['movableItems'] || [];

		if (!Array.isArray(movableItemsData)) {
			throw new Error('Invalid movable items data.');
		}

		const movableItems = new Set<MovableItem>();
		const movableItemLocations = new Map<MovableItem, MovableItemLocation>();

		for (const movableItemData of movableItemsData) {
			const { movableItem, movableItemLocation } = this._getMovableItemForData(
				movableItemData,
				gamePlugins,
			);

			movableItems.add(movableItem);
			movableItemLocations.set(movableItem, movableItemLocation);
		}

		return { movableItems, movableItemLocations };
	}

	private _getLocationData(levelData: RawData): RawData {
		const locationData = levelData['location'];

		if (!locationData) {
			throw new Error('No location data in MovableItem data.');
		}

		return locationData;
	}

	private _getInstructions(levelData: RawData): InstructionCounts {
		const instructionsData = levelData['instructions'];
		if (!instructionsData) {
			throw new Error('No instructions in level data.');
		}

		const instructionCounts: InstructionCounts = new Map();

		if (levelData['instructions']) {
			for (const typeName in instructionsData) {
				const type = getInstructionType(typeName);
				instructionCounts.set(type, levelData['instructions'][typeName]);
			}
		}

		return instructionCounts;
	}
}
